import {
  Component,
  OnInit,
  ChangeDetectionStrategy,
  Input,
  forwardRef,
  EventEmitter,
  Output,
  AfterViewInit,
  ChangeDetectorRef,
} from '@angular/core';
import {
  ControlValueAccessor,
  UntypedFormArray,
  UntypedFormBuilder,
  UntypedFormGroup,
  NG_VALUE_ACCESSOR,
  Validators,
} from '@angular/forms';
import { debounceTime } from 'rxjs/operators';
import format from 'date-fns/format';
import addMinutes from 'date-fns/addMinutes';
import { CalendarService, ForecastWeightsEnum } from '../../../index';
import { ForecastSettingsData, ISchedule } from '../../../models/interfaces';
import { DecorateUntilDestroy, takeUntilDestroyed } from '@skyeng/libs/shared/rxjs';
import { BehaviorSubject, Observable } from 'rxjs';
import range from 'lodash/range';
import { ForecastHelperService } from '../../../services/forecast-helper.service';

@Component({
  selector: 'app-custom-time-table-weights',
  templateUrl: './custom-time-weights.component.html',
  styleUrls: ['./custom-time-weights.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => CustomTimeWeightsComponent),
      multi: true,
    },
  ],
})
@DecorateUntilDestroy()
export class CustomTimeWeightsComponent implements OnInit, ControlValueAccessor, AfterViewInit {
  @Input() data: ForecastSettingsData = {
    date: new Date(),
    slotSchedule: {} as ISchedule,
    timeWeights: [[]],
    customTimeWeights: {},
    dayWeights: [],
  };
  @Input() changeCustomInterval$ = new Observable<string[]>();
  @Output() customTimeWeightsEvent = new EventEmitter<number[][]>();
  form: UntypedFormGroup;
  customTimeWeightsValid: Function;
  customTimeWeightsDataSource: string[][] = [];
  forecastWeightsEnum = ForecastWeightsEnum;
  customTimeWeightsColumnsToDisplay: string[] = [];
  currentDays$ = new BehaviorSubject<string[]>([]);
  getIteration = this.forecastHelperService.getIteration;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private calendarService: CalendarService,
    private forecastHelperService: ForecastHelperService,
    private changeDetectorRefs: ChangeDetectorRef,
  ) {}

  ngOnInit(): void {
    this.customTimeWeightsValid = this.forecastHelperService.getCustomTimeWeights;
    const timeWeightsControls = this.getCustomTimeWeightsControls(this.data.customTimeWeights);
    this.form = this.formBuilder.group({
      [ForecastWeightsEnum.customTimeWeightsGroup]: timeWeightsControls,
    });

    this.form
      .get(ForecastWeightsEnum.customTimeWeightsGroup)
      ?.valueChanges.pipe(debounceTime(100), takeUntilDestroyed(this))
      .subscribe(item => {
        this.onChange(item);
        this.writeValue(item);
      });

    this.currentDays$.next(Object.keys(this.data.customTimeWeights));
    this.changeCustomInterval$.pipe(takeUntilDestroyed(this)).subscribe((event: string[]) => {
      this.currentDays$.next(event);
      this.changeDetectorRefs.detectChanges();
    });
  }

  ngAfterViewInit(): void {
    const timeWeightsGroupValue = this.form.get(ForecastWeightsEnum.customTimeWeightsGroup)?.value;
    this.onChange(timeWeightsGroupValue);
    this.writeValue(timeWeightsGroupValue);
  }

  isInvalid(formGroupName: number, formControlName: number): boolean {
    const forecastWeightsControls = (this.form.get(ForecastWeightsEnum.customTimeWeightsGroup) as UntypedFormGroup)
      ?.controls;
    if (forecastWeightsControls && forecastWeightsControls[formGroupName]) {
      return (forecastWeightsControls[formGroupName] as UntypedFormGroup)?.controls[formControlName].invalid;
    } else {
      return false;
    }
  }

  private getCustomTimeWeightsControls(customTimeWeights: { [key: string]: number[] }): UntypedFormArray {
    let timeWeightsControls: UntypedFormArray[] = [];
    const iteration = this.forecastHelperService.getIteration(this.data.slotSchedule, this.data.date);
    if (iteration) {
      const duration = iteration.slotDuration;
      const start = this.calendarService.serverDateTime(iteration.dailyStartOffset);
      this.customTimeWeightsDataSource = [];
      // eslint-disable-next-line id-length
      for (let s = 0; s < iteration.dailySlotCount; s++) {
        let daysSlotsConfig: unknown[] = [];
        let daySlotsTimeWeightsDataSet: string[] = [format(addMinutes(start, s * duration), 'HH:mm')];
        // eslint-disable-next-line id-length
        for (let day = 1; day <= 31; day++) {
          const dayWeights = customTimeWeights[day] || [];
          const daySlotTimeWeight = dayWeights[s] || 0;
          daySlotsTimeWeightsDataSet = [...daySlotsTimeWeightsDataSet, day.toString()];
          daysSlotsConfig = [...daysSlotsConfig, [daySlotTimeWeight, [Validators.required, Validators.min(0)]]];
        }

        timeWeightsControls = [...timeWeightsControls, this.formBuilder.array(daysSlotsConfig)];
        this.customTimeWeightsDataSource = [...this.customTimeWeightsDataSource, daySlotsTimeWeightsDataSet];
      }

      this.customTimeWeightsColumnsToDisplay = [
        'wfmModule.entities.slot',
        ...range(1, 32).map(item => item.toString()),
      ];
    }

    return this.formBuilder.array(timeWeightsControls);
  }

  uploadCustomTimeWeights(timeWeights: number[][]): void {
    (this.form.get(ForecastWeightsEnum.customTimeWeightsGroup) as UntypedFormArray).controls.forEach((item, index) => {
      (item as UntypedFormArray).controls.forEach((item, index2: number) => {
        item.setValue((timeWeights[index] || [])[index2] || 0);
      });
    });
  }

  writeValue(timeWeights: number[][]): void {
    this.customTimeWeightsEvent.emit(timeWeights);
  }

  onChange: (value: string) => void = () => {};
  onTouched = (): void => {};

  registerOnChange(fn: () => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }
}
